import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowDownRight, ShieldCheck, Landmark, Globe2, Layers, Zap } from "lucide-react";
import { api, formatPct } from "@/lib/api";
import { NAV } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import OrbitalLogo from "@/components/OrbitalLogo";

const HERO_IMG = "https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMGRhcmt8ZW58MHx8fHwxNzgxMDQ5NTg5fDA&ixlib=rb-4.1.0&q=85";
const BANK_IMG = "https://images.unsplash.com/photo-1609358905581-e5381612486e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBiYW5rJTIwdmF1bHR8ZW58MHx8fHwxNzgxMDQ5NTg4fDA&ixlib=rb-4.1.0&q=85";
const MOBILE_IMG = "https://images.unsplash.com/photo-1770739982427-3169499c332e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxwZXJzb24lMjB1c2luZyUyMG1vYmlsZSUyMHBob25lJTIwZGFya3xlbnwwfHx8fDE3ODEwNDk1ODl8MA&ixlib=rb-4.1.0&q=85";

const Ticker = ({ assets }) => {
  if (!assets.length) return null;
  const items = [...assets, ...assets];
  return (
    <div className="overflow-hidden border-y border-[#2A2E38] bg-[#0B0D12]">
      <div className="marquee-track whitespace-nowrap py-3">
        {items.map((a, i) => (
          <span key={`${a.symbol}-${i}`} className="inline-flex items-center gap-3 px-6 font-mono text-xs">
            <span className="text-[#A3A9B8]">{a.symbol}</span>
            <span className="text-[#F4F1EB]">${Number(a.price_usd).toLocaleString()}</span>
            <span className={a.change_24h >= 0 ? "text-[#7C9C69]" : "text-[#C75C5C]"}>
              {formatPct(a.change_24h)}
            </span>
            <span className="text-[#2A2E38]">·</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default function Landing() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);

  useEffect(() => {
    const fetchData = () => api.get("/markets").then((r) => setAssets(r.data.assets)).catch(() => {});
    fetchData();
    const id = setInterval(fetchData, 30_000);
    return () => clearInterval(id);
  }, []);

  const openAsset = (symbol) => {
    if (user) navigate(`/app/markets/${symbol}`);
    else navigate("/login", { state: { from: { pathname: `/app/markets/${symbol}` } } });
  };

  const heroTop = assets.slice(0, 6);

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F4F1EB]">
      {/* Top nav */}
      <header className="border-b border-[#2A2E38]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <OrbitalLogo size={36} />
            <div>
              <div className="font-display text-lg sm:text-xl font-bold leading-none">Orbital</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587] mt-1">Exchange</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#A3A9B8]">
            <a href="#markets" className="hover:text-[#F4F1EB] transition-colors">{t("nav.markets")}</a>
            <a href="#features" className="hover:text-[#F4F1EB] transition-colors">{t("nav.features")}</a>
            <a href="#fiat" className="hover:text-[#F4F1EB] transition-colors">{t("nav.fiat")}</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            {user ? (
              <Link
                to="/app/dashboard"
                data-testid={NAV.landingCtaDashboard}
                className="px-3 sm:px-4 py-2 bg-[#7C5CFF] text-white text-xs sm:text-sm rounded-sm hover:bg-[#9C82FF] transition-colors"
              >
                {t("nav.openapp")}
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid={NAV.landingCtaLogin}
                  className="text-xs sm:text-sm text-[#A3A9B8] hover:text-[#F4F1EB] px-2 sm:px-3 py-2"
                >
                  {t("nav.signin")}
                </Link>
                <Link
                  to="/register"
                  data-testid={NAV.landingCtaRegister}
                  className="px-3 sm:px-4 py-2 bg-[#7C5CFF] text-white text-xs sm:text-sm rounded-sm hover:bg-[#9C82FF] transition-colors whitespace-nowrap"
                >
                  {t("nav.getstarted")}
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative grain overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0D12]/40 via-[#0B0D12]/70 to-[#0B0D12]"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-36">
          <div className="text-xs tracking-[0.3em] uppercase text-[#7C5CFF] mb-6 reveal">{t("hero.eyebrow")}</div>
          <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight max-w-5xl reveal" style={{ animationDelay: "0.1s" }}>
            {t("hero.title1")} <br />
            <span className="text-[#7C5CFF]">{t("hero.title2")}</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#A3A9B8] leading-relaxed reveal" style={{ animationDelay: "0.25s" }}>
            {t("hero.subtitle")}
          </p>
          <div className="mt-10 flex flex-wrap gap-3 reveal" style={{ animationDelay: "0.4s" }}>
            {!user && (
              <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] transition-colors">
                {t("hero.cta_create")} <ArrowUpRight size={16} />
              </Link>
            )}
            <Link to={user ? "/app/markets" : "/login"} className="inline-flex items-center gap-2 px-6 py-3 border border-[#2A2E38] text-[#F4F1EB] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] transition-colors">
              {t("hero.cta_explore")}
            </Link>
          </div>

          {/* Live top assets pills */}
          <div className="mt-16 flex flex-wrap gap-2 reveal" style={{ animationDelay: "0.55s" }}>
            {heroTop.map((a) => (
              <button
                key={a.symbol}
                onClick={() => openAsset(a.symbol)}
                data-testid={`landing-hero-pill-${a.symbol}`}
                className="border border-[#2A2E38] bg-[#141720]/70 backdrop-blur px-4 py-2 rounded-sm flex items-center gap-3 font-mono text-xs hover:border-[#7C5CFF] hover:text-[#7C5CFF] transition-colors"
              >
                <span className="text-[#A3A9B8]">{a.symbol}</span>
                <span>${Number(a.price_usd).toLocaleString()}</span>
                <span className={a.change_24h >= 0 ? "text-[#7C9C69]" : "text-[#C75C5C]"}>
                  {formatPct(a.change_24h)}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <Ticker assets={assets} />

      {/* Bento features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#6E7587] mb-3">{t("features.eyebrow")}</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-2xl">
              {t("features.title")}
            </h2>
          </div>
          <Link to={user ? "/app/dashboard" : "/register"} className="text-sm text-[#7C5CFF] hover:text-[#9C82FF] inline-flex items-center gap-1">
            {t("features.cta")} <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2A2E38]">
          {/* Big card */}
          <div className="md:col-span-2 md:row-span-2 bg-[#141720] p-8 relative overflow-hidden min-h-[320px]">
            <Layers size={28} className="text-[#7C5CFF]" />
            <h3 className="font-display text-3xl font-bold mt-6 max-w-md">{t("features.bento1.title")}</h3>
            <p className="mt-4 text-[#A3A9B8] max-w-md">
              {t("features.bento1.desc")}
            </p>
            <div className="absolute right-6 bottom-6 opacity-40 font-mono text-[10px] tracking-widest text-[#6E7587]">
              MARKETS · LIVE
            </div>
          </div>

          {/* Bank link */}
          <div id="fiat" className="bg-[#141720] relative overflow-hidden min-h-[200px] group">
            <img src={BANK_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#0B0D12]/40 to-[#141720]/95"></div>
            <div className="relative p-8">
              <Landmark size={24} className="text-[#E8E2D9]" />
              <h3 className="font-display text-xl font-bold mt-4">{t("features.bento2.title")}</h3>
              <p className="mt-2 text-sm text-[#A3A9B8]">
                {t("features.bento2.desc")}
              </p>
            </div>
          </div>

          <div className="bg-[#141720] p-8 min-h-[200px]">
            <Globe2 size={24} className="text-[#7C5CFF]" />
            <h3 className="font-display text-xl font-bold mt-4">{t("features.bento3.title")}</h3>
            <p className="mt-2 text-sm text-[#A3A9B8]">
              {t("features.bento3.desc")}
            </p>
          </div>

          <div className="bg-[#141720] p-8 min-h-[200px]">
            <ShieldCheck size={24} className="text-[#7C9C69]" />
            <h3 className="font-display text-xl font-bold mt-4">{t("features.bento4.title")}</h3>
            <p className="mt-2 text-sm text-[#A3A9B8]">
              {t("features.bento4.desc")}
            </p>
          </div>

          <div className="md:col-span-2 bg-[#141720] p-8 relative overflow-hidden min-h-[220px]">
            <img src={MOBILE_IMG} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#141720] via-[#141720]/85 to-transparent"></div>
            <div className="relative max-w-md">
              <Zap size={24} className="text-[#7C5CFF]" />
              <h3 className="font-display text-2xl font-bold mt-4">{t("features.bento5.title")}</h3>
              <p className="mt-2 text-sm text-[#A3A9B8]">
                {t("features.bento5.desc")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Markets table */}
      <section id="markets" className="max-w-7xl mx-auto px-6 pb-24">
        <div className="border border-[#2A2E38] bg-[#141720]">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#2A2E38]">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#6E7587]">{t("markets.eyebrow")}</div>
              <h3 className="font-display text-2xl font-bold mt-1">{t("markets.title")}</h3>
            </div>
            <span className="text-xs text-[#6E7587] font-mono inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7C9C69] live-dot"></span>
              {t("markets.autorefresh")}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] tracking-[0.2em] uppercase text-[#6E7587]">
                <tr className="border-b border-[#2A2E38]">
                  <th className="text-left px-6 py-3 font-normal">{t("markets.col_asset")}</th>
                  <th className="text-right px-6 py-3 font-normal">{t("markets.col_price")}</th>
                  <th className="text-right px-6 py-3 font-normal">{t("markets.col_24h")}</th>
                  <th className="text-right px-6 py-3 font-normal hidden sm:table-cell">{t("markets.col_mcap")}</th>
                </tr>
              </thead>
              <tbody>
                {assets.slice(0, 8).map((a) => (
                  <tr
                    key={a.symbol}
                    data-testid={`landing-asset-row-${a.symbol}`}
                    onClick={() => openAsset(a.symbol)}
                    className="border-b border-[#2A2E38] hover:bg-[#1C202B] transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4 flex items-center gap-3">
                      {a.image && <img src={a.image} alt={a.symbol} className="w-6 h-6" />}
                      <div>
                        <div className="font-medium">{a.name}</div>
                        <div className="text-xs text-[#6E7587] font-mono">{a.symbol}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right font-mono">${Number(a.price_usd).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    <td className={`px-6 py-4 text-right font-mono ${a.change_24h >= 0 ? "text-[#7C9C69]" : "text-[#C75C5C]"}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {a.change_24h >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                        {formatPct(a.change_24h)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-[#A3A9B8] hidden sm:table-cell">
                      ${Number(a.market_cap || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                  </tr>
                ))}
                {assets.length === 0 && (
                  <tr><td colSpan={4} className="px-6 py-8 text-center text-[#6E7587]">{t("markets.loading")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#2A2E38] py-10 text-center text-xs text-[#6E7587]">
        {t("footer.tag")}
      </footer>
    </div>
  );
}
